/* assets/soho-video-reels.js  —  SOHO Bohemian
 *
 * One custom element per section: <soho-reels>. It owns ONLY the cards inside
 * itself.
 *
 * HARD RULE (correction 7): this file never calls
 * document.querySelectorAll('video'). Every lookup is rooted at `this`.
 * A global video query here would pause the "On the girls" rail on the nine
 * live /collections/<set>-set pages and the home background video.
 *
 * No scroll listener is ever throttled with requestAnimationFrame — a rAF
 * callback does not fire inside a throttled context, which is how a scroll
 * handler ends up never running (correction 11). Visibility is
 * IntersectionObserver; arrow state is a plain passive scroll listener doing
 * two number comparisons.
 *
 * PLAYBACK POLICY (correction: "one at a time" made only card 1 play):
 *   desktop  (min-width:750px AND pointer:fine) -> every card >= 50% visible
 *                                                  plays, capped at 6
 *   phone / touch / narrow                      -> only the most-visible card
 * Reduced motion turns autoplay off entirely; tapping still plays.
 *
 * SHOP THE VIDEO (Dylan, 29/08: "the girls have an outfit full of many items
 * and i want them to be able to shop one or multiple items"). The bag button
 * used to be nothing but an <a href> to one PDP. It now hands the card's
 * payload to the shared modal, which shows the whole outfit as a strip of
 * product cards. Three rules keep the store standing when anything is missing:
 *
 *   1. the modal asset is looked up AT CLICK TIME, never at boot. It is a
 *      separate <script> and it may not have executed yet, or at all;
 *   2. every bail-out path leaves the click alone. No preventDefault means the
 *      <a href> navigates exactly as it did before this feature existed, which
 *      is contract rule 4 ("p vazio E c ausente => o card NAO ganha modal");
 *   3. preventDefault is only spent once we are certain we are opening
 *      something. A swallowed click that opens nothing is a dead button.
 *
 * The listener is delegated on the TRACK, not bound per card, because
 * soho-video-config.js empties the track and rebuilds every card from the
 * dashboard config — per-card listeners would die with the cards they were on.
 *
 * The hard rule above still holds: the only document-level query added here
 * looks for the modal's own overlay (`.soho-shopv.is-open`), never for a
 * <video>. Every video this file pauses or plays still comes out of `this`.
 */
(function () {
  'use strict';

  if (window.customElements && customElements.get('soho-reels')) return;

  var SEL = {
    card: '[data-soho-reel]',
    media: '[data-soho-reel-media]',
    player: '[data-soho-reel-player]',
    template: '[data-soho-reel-template]',
    toggle: '[data-soho-reel-toggle]',
    track: '[data-soho-reel-track]',
    prev: '[data-soho-reel-prev]',
    next: '[data-soho-reel-next]',
    bag: '[data-soho-reel-bag]',
    shopData: '[data-soho-shop]'
  };
  var CLASS_PLAYING = 'is-playing';
  var PLAY_FLOOR = 0.5;
  var MAX_CONCURRENT = 6;
  var HYDRATE_MARGIN = '300px';

  /* The modal is written by another file and loaded by both sections. These
     two document events are how it tells everyone it went up and came down;
     they are also the only names this file listens for. */
  var SHOP_OPEN_EVENT = 'soho:shopv:open';
  var SHOP_CLOSE_EVENT = 'soho:shopv:close';
  /* Contract section 2: `.soho-shopv.is-open` is a state class the modal
     writes. Reading it is the watchdog's last resort — see watchShopClose. */
  var SHOP_OPEN_ROOT = '.soho-shopv.is-open';
  var SHOP_WATCH_TICK = 400;
  var SHOP_WATCH_GRACE = 1500;

  function mq(query) {
    return window.matchMedia ? window.matchMedia(query) : { matches: false, addEventListener: null };
  }

  /* "Is there anything at all to sell here?" and nothing more. The payload has
     already been through the modal's readPayload, which is the ONE place that
     validates handles (contract section 7) — re-validating them here is how
     two copies of a regex drift apart. Contract rule 4: no products and no
     collection means no modal, so the bag stays a plain link to the PDP. */
  function sellable(payload) {
    if (!payload) return false;
    if (payload.p && payload.p.length) return true;
    return !!(payload.c && payload.c.h);
  }

  /* Modifier and middle clicks belong to the browser: open in a new tab, a new
     window, download, "save link as". Intercepting those is the classic way a
     shop link stops being a link. A gesture somebody else has already handled
     is not ours either. */
  function plainGesture(event) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    return !event.defaultPrevented;
  }

  /* Shopify hands back several renditions of one video, and on some assets an
     HLS manifest sits in the list. The modal paints a plain <video src>, where
     only Safari can read a .m3u8 — so an mp4 wins whenever there is one, and
     the first usable source is the fallback. */
  function pickSource(nodes) {
    var i;
    var src;
    var type;
    if (!nodes || !nodes.length) return '';
    for (i = 0; i < nodes.length; i++) {
      src = nodes[i].getAttribute('src') || '';
      type = (nodes[i].getAttribute('type') || '').toLowerCase();
      if (src && (type.indexOf('mp4') > -1 || src.toLowerCase().indexOf('.mp4') > -1)) return src;
    }
    for (i = 0; i < nodes.length; i++) {
      src = nodes[i].getAttribute('src') || '';
      if (src) return src;
    }
    return '';
  }

  customElements.define(
    'soho-reels',
    class SohoReels extends HTMLElement {
      constructor() {
        super();
        this.cards = [];
        this.ratios = [];
        this.hydrateObserver = null;
        this.playObserver = null;
        this.resizeObserver = null;
        this.resizeFallbackBound = false;
        /* Shop-the-video state. `suspended` doubles as the "the modal is up and
           we parked playback" flag, so it is null (not []) when it is not. */
        this.shopRoot = null;
        this.shopOpen = false;
        this.suspended = null;
        this.shopTrigger = null;
        this.shopWatch = 0;
        this.shopSawOpen = false;
        this.onToggleClick = this.onToggleClick.bind(this);
        this.onArrowClick = this.onArrowClick.bind(this);
        this.onTrackScroll = this.onTrackScroll.bind(this);
        this.onPolicyChange = this.onPolicyChange.bind(this);
        this.onBlockSelect = this.onBlockSelect.bind(this);
        this.refreshChrome = this.refreshChrome.bind(this);
        this.onTrackClick = this.onTrackClick.bind(this);
        this.onTrackKeydown = this.onTrackKeydown.bind(this);
        this.onShopOpenEvent = this.onShopOpenEvent.bind(this);
        /* Bound once and reused as the modal's `onClose` callback, so passing
           it twice cannot produce two different functions. */
        this.onShopClose = this.onShopClose.bind(this);
      }

      connectedCallback() {
        this.cards = Array.prototype.slice.call(this.querySelectorAll(SEL.card));
        if (!this.cards.length) return;

        this.track = this.querySelector(SEL.track);
        this.prevButton = this.querySelector(SEL.prev);
        this.nextButton = this.querySelector(SEL.next);

        this.reducedMotionQuery = mq('(prefers-reduced-motion: reduce)');
        this.desktopQuery = mq('(min-width: 750px) and (pointer: fine)');
        this.autoplay = this.getAttribute('data-autoplay') !== 'false' && !this.reducedMotionQuery.matches;

        this.ratios = this.cards.map(function () {
          return 0;
        });

        this.cards.forEach(function (card, i) {
          card.setAttribute('data-reel-index', String(i));
          var toggle = card.querySelector(SEL.toggle);
          if (toggle) toggle.addEventListener('click', this.onToggleClick);
        }, this);

        this.bindChrome();
        this.bindObservers();
        this.bindShop();

        if (this.reducedMotionQuery && this.reducedMotionQuery.addEventListener) {
          this.reducedMotionQuery.addEventListener('change', this.onPolicyChange);
        }
        if (this.desktopQuery && this.desktopQuery.addEventListener) {
          this.desktopQuery.addEventListener('change', this.onPolicyChange);
        }

        /* Theme editor: bring the block Dylan just clicked into view. */
        document.addEventListener('shopify:block:select', this.onBlockSelect);
      }

      disconnectedCallback() {
        this.unbindShop();
        if (this.hydrateObserver) this.hydrateObserver.disconnect();
        if (this.playObserver) this.playObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();

        this.cards.forEach(function (card) {
          var toggle = card.querySelector(SEL.toggle);
          if (toggle) toggle.removeEventListener('click', this.onToggleClick);
        }, this);

        if (this.resizeFallbackBound) {
          window.removeEventListener('resize', this.refreshChrome);
          this.resizeFallbackBound = false;
        }

        if (this.track) this.track.removeEventListener('scroll', this.onTrackScroll);
        if (this.prevButton) this.prevButton.removeEventListener('click', this.onArrowClick);
        if (this.nextButton) this.nextButton.removeEventListener('click', this.onArrowClick);
        if (this.reducedMotionQuery && this.reducedMotionQuery.removeEventListener) {
          this.reducedMotionQuery.removeEventListener('change', this.onPolicyChange);
        }
        if (this.desktopQuery && this.desktopQuery.removeEventListener) {
          this.desktopQuery.removeEventListener('change', this.onPolicyChange);
        }
        document.removeEventListener('shopify:block:select', this.onBlockSelect);
      }

      /* ---------------------------------------------------------------- rail */

      bindChrome() {
        /* Grid layout has no arrows: nothing to wire up. */
        if (!this.track || !this.prevButton || !this.nextButton) return;

        /* Plain passive listener. No rAF throttle: see the header note. */
        this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
        if (this.prevButton) this.prevButton.addEventListener('click', this.onArrowClick);
        if (this.nextButton) this.nextButton.addEventListener('click', this.onArrowClick);

        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(this.refreshChrome);
          this.resizeObserver.observe(this.track);
        } else {
          /* Flagged so disconnectedCallback can take it back off again. The
             theme editor tears this element down and rebuilds it on every
             setting change, so an un-removed window listener would pile up one
             dead copy per keystroke. */
          window.addEventListener('resize', this.refreshChrome);
          this.resizeFallbackBound = true;
        }
        this.refreshChrome();
      }

      /* Arrows exist only when the row really overflows. They are also centred
         on the MEDIA box, not on the card: the caption and price sit below the
         video and would drag the arrows off-centre (this is precisely what the
         theme's own GridSlider.positionArrows does wrong for a 9:16 rail). */
      refreshChrome() {
        if (!this.track || !this.prevButton || !this.nextButton) return;

        var media = this.querySelector(SEL.media);
        if (media && media.offsetHeight) {
          this.style.setProperty('--soho-arrow-top', media.offsetHeight / 2 + 'px');
        }

        var overflows = this.track.scrollWidth - this.track.clientWidth > 2;
        this.prevButton.hidden = !overflows;
        this.nextButton.hidden = !overflows;
        this.onTrackScroll();
      }

      onTrackScroll() {
        if (!this.track || !this.prevButton || !this.nextButton) return;
        var max = this.track.scrollWidth - this.track.clientWidth;
        var x = this.track.scrollLeft;
        this.prevButton.disabled = x <= 2;
        this.nextButton.disabled = x >= max - 2;
      }

      onArrowClick(event) {
        if (!this.track) return;
        var dir = event.currentTarget === this.prevButton ? -1 : 1;
        /* One card + one gap, measured rather than guessed. */
        var step;
        if (this.cards.length > 1) step = this.cards[1].offsetLeft - this.cards[0].offsetLeft;
        else if (this.cards[0]) step = this.cards[0].getBoundingClientRect().width;
        else step = this.track.clientWidth * 0.8;
        if (!step) step = this.track.clientWidth * 0.8;
        this.track.scrollBy({
          left: step * dir,
          top: 0,
          behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth'
        });
      }

      onBlockSelect(event) {
        if (!this.contains(event.target)) return;
        if (!this.track || typeof event.target.scrollIntoView !== 'function') return;
        event.target.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }

      /* ------------------------------------------------------------ playback */

      /* Split in two on purpose. onPolicyChange fires on every
         reduced-motion / breakpoint change and only ever needs the PLAY
         observer rebuilt; it used to call bindObservers(), which built a second
         hydrate observer over the same cards without disconnecting the first. */
      bindObservers() {
        this.bindHydrateObserver();
        this.bindPlayObserver();
      }

      /* Hydration is independent of autoplay: a tap must be able to play a
         card even when autoplay is off or motion is reduced. Injecting the
         markup costs nothing — the <video> is preload="none". */
      bindHydrateObserver() {
        if (!('IntersectionObserver' in window)) return;
        if (this.hydrateObserver) this.hydrateObserver.disconnect();

        this.hydrateObserver = new IntersectionObserver(
          function (entries, observer) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              this.mount(entry.target);
              observer.unobserve(entry.target);
            }, this);
          }.bind(this),
          { root: null, rootMargin: HYDRATE_MARGIN, threshold: 0 }
        );
        this.cards.forEach(function (card) {
          /* Already mounted: nothing left to hydrate. */
          if (card.querySelector('video')) return;
          this.hydrateObserver.observe(card);
        }, this);
      }

      bindPlayObserver() {
        if (this.playObserver) {
          this.playObserver.disconnect();
          this.playObserver = null;
        }
        if (!this.autoplay) return;
        if (!('IntersectionObserver' in window)) return;

        this.playObserver = new IntersectionObserver(this.onIntersect.bind(this), {
          root: null,
          threshold: [0, 0.25, 0.5, 0.75, 1]
        });
        this.cards.forEach(function (card) {
          this.playObserver.observe(card);
        }, this);
      }

      onPolicyChange() {
        this.autoplay = this.getAttribute('data-autoplay') !== 'false' && !this.reducedMotionQuery.matches;
        this.bindPlayObserver();
        if (!this.autoplay) {
          this.pauseAll();
          return;
        }
        this.applyPolicy();
      }

      /* Replaces the <template> with real markup exactly once.
         Returns the <video>, or null when the card is a still. */
      mount(card) {
        var player = card.querySelector(SEL.player);
        if (!player) return null;

        var existing = player.querySelector('video');
        if (existing) return existing;

        var tpl = player.querySelector(SEL.template);
        if (!tpl) return null;

        player.innerHTML = tpl.innerHTML;
        var video = player.querySelector('video');
        if (!video) return null;

        /* Belt and braces: some engines do not honour the muted attribute when
           the element arrives through innerHTML, and an unmuted video is
           refused autoplay everywhere. */
        if (video.hasAttribute('muted')) video.muted = true;
        video.setAttribute('playsinline', '');

        var toggle = card.querySelector(SEL.toggle);
        video.addEventListener('play', function () {
          card.classList.add(CLASS_PLAYING);
          if (toggle) toggle.setAttribute('aria-pressed', 'true');
        });
        video.addEventListener('pause', function () {
          card.classList.remove(CLASS_PLAYING);
          if (toggle) toggle.setAttribute('aria-pressed', 'false');
        });
        return video;
      }

      /* An autoplay attempt is REFUSED by every browser when the video is not
         muted (Dylan can turn "Mute" off in the editor, and the editor warns
         him there). The refusal must not leave a half-started card behind:
         some engines fire `play` and only then reject, which would leave
         .is-playing on — a faded-out poster over a frozen video, i.e. a blank
         card. So on rejection we put the card back to its poster, which is a
         legitimate still with a play arrow on it, and stop retrying that video
         on every scroll tick. A real tap clears the flag and tries again — a
         user gesture is allowed to play with sound. */
      safePlay(video, userInitiated) {
        if (!video || typeof video.play !== 'function') return;
        /* A tap always gets a fresh attempt — a user gesture is allowed to play
           with sound. Autoplay does not re-ask a video the browser has already
           refused, so an unmuted rail stops churning through rejected play()
           calls on every scroll tick. */
        if (!userInitiated && video.getAttribute('data-soho-autoplay-blocked') === 'true') return;

        var self = this;
        var promise;
        try {
          promise = video.play();
        } catch (e) {
          self.showPoster(video);
          return;
        }
        if (promise && promise.catch) {
          promise.catch(function () {
            self.showPoster(video);
          });
        }
      }

      /* Back to the cover image + play arrow, with aria-pressed honest. */
      showPoster(video) {
        video.setAttribute('data-soho-autoplay-blocked', 'true');
        if (!video.paused) {
          video.pause();
          return; /* the pause handler does the class + aria work */
        }
        var card = video.closest ? video.closest(SEL.card) : null;
        if (!card) return;
        card.classList.remove(CLASS_PLAYING);
        var toggle = card.querySelector(SEL.toggle);
        if (toggle) toggle.setAttribute('aria-pressed', 'false');
      }

      /* Pauses only the videos this element created. Scoped by construction:
         `card` always comes out of this.cards. */
      pauseCard(card) {
        var video = card.querySelector('video');
        if (video && !video.paused) video.pause();
      }

      pauseAll() {
        this.cards.forEach(this.pauseCard, this);
      }

      onIntersect(entries) {
        entries.forEach(function (entry) {
          var i = Number(entry.target.getAttribute('data-reel-index'));
          if (!isNaN(i)) this.ratios[i] = entry.intersectionRatio;
        }, this);
        this.applyPolicy();
      }

      applyPolicy() {
        if (!this.autoplay) return;
        /* The shop modal owns the screen — and the sound — while it is up. The
           page behind it is scroll-locked, but the modal's own layout can still
           produce an intersection tick, and without this line that tick would
           quietly restart a card behind the scrim. suspendPlayback() remembers
           what was playing; onShopClose() puts exactly that back. */
        if (this.shopOpen) return;

        var wantsMany = this.desktopQuery.matches;
        var shouldPlay = {};

        if (wantsMany) {
          var picked = 0;
          this.ratios.forEach(function (ratio, i) {
            if (ratio >= PLAY_FLOOR && picked < MAX_CONCURRENT) {
              shouldPlay[i] = true;
              picked++;
            }
          });
        } else {
          var bestIndex = -1;
          var best = PLAY_FLOOR;
          this.ratios.forEach(function (ratio, i) {
            if (ratio > best) {
              best = ratio;
              bestIndex = i;
            }
          });
          if (bestIndex > -1) shouldPlay[bestIndex] = true;
        }

        this.cards.forEach(function (card, i) {
          if (shouldPlay[i]) this.safePlay(this.mount(card));
          else this.pauseCard(card);
        }, this);
      }

      /* Tap / Enter / Space on the media toggles that one card. It is a real
         <button>, so the keyboard comes for free and the bag link (a sibling
         with a higher z-index) keeps its own click. */
      onToggleClick(event) {
        event.preventDefault();
        var card = event.currentTarget.closest(SEL.card);
        if (!card) return;

        var video = this.mount(card);
        if (!video) return;

        if (video.paused) {
          if (!this.desktopQuery.matches) {
            /* On a phone, one at a time stays the rule even for taps. */
            this.cards.forEach(function (other) {
              if (other !== card) this.pauseCard(other);
            }, this);
          }
          this.safePlay(video, true);
        } else {
          video.pause();
        }
      }

      /* --------------------------------------------------- shop the video */

      /* Delegated on the track, for the reason in the file header: the
         dashboard rebuilds the cards, so only a listener that outlives them is
         worth having. Grid layouts have no arrows but they do have a track;
         falling back to `this` only covers a future markup change that drops
         it, and keeps the handler scoped to this element either way. */
      bindShop() {
        this.shopRoot = this.track || this;
        this.shopRoot.addEventListener('click', this.onTrackClick);
        this.shopRoot.addEventListener('keydown', this.onTrackKeydown);
        /* A second reel row (or any other surface) opening the modal has to
           quiet this row too, otherwise two soundtracks play at once. */
        document.addEventListener(SHOP_OPEN_EVENT, this.onShopOpenEvent);
        document.addEventListener(SHOP_CLOSE_EVENT, this.onShopClose);
      }

      unbindShop() {
        if (this.shopRoot) {
          this.shopRoot.removeEventListener('click', this.onTrackClick);
          this.shopRoot.removeEventListener('keydown', this.onTrackKeydown);
          this.shopRoot = null;
        }
        document.removeEventListener(SHOP_OPEN_EVENT, this.onShopOpenEvent);
        document.removeEventListener(SHOP_CLOSE_EVENT, this.onShopClose);
        this.stopShopWatch();
        /* Torn down while the modal was up — the dashboard rebuild re-attaches
           this very element, and the theme editor rebuilds it on every
           keystroke. Nothing will ever tell us it closed, so drop the parked
           state instead of leaving autoplay switched off for good. */
        this.shopOpen = false;
        this.suspended = null;
        this.shopTrigger = null;
      }

      /* Click on the bag. Every `return` here is deliberate: it means the
         browser keeps the click and the <a href> navigates to the PDP, which
         is the behaviour this feature replaced and therefore the only safe
         thing to fall back to. */
      onTrackClick(event) {
        if (event.button) return; /* middle / right: not ours */
        if (!plainGesture(event)) return;

        var plan = this.shopTargetFor(event);
        if (!plan) return;

        event.preventDefault();
        this.launchShop(plan);
      }

      /* Enter needs nothing: the bag is a real <a href>, and Enter on a focused
         link dispatches a click, which onTrackClick above already owns. Adding
         Enter here as well would open the modal twice. Space is the gap — on an
         anchor it scrolls the page instead of activating the link — so it is
         handled, and ONLY it.
         preventDefault is again spent last: when there is nothing to shop, the
         key keeps its native meaning and the page scrolls as it always did. */
      onTrackKeydown(event) {
        var isSpace = event.key === ' ' || event.key === 'Spacebar' || event.keyCode === 32;
        if (!isSpace || event.repeat) return;
        if (!plainGesture(event)) return;

        var plan = this.shopTargetFor(event);
        if (!plan) return;

        event.preventDefault();
        this.launchShop(plan);
      }

      /* One event -> "a bag, on one of MY cards, with something to sell", or
         null. Both entry points go through it, so the pointer path and the
         keyboard path cannot drift on what counts as shoppable. */
      shopTargetFor(event) {
        var target = event.target;
        var bag = target && target.closest ? target.closest(SEL.bag) : null;
        if (!bag || !this.contains(bag)) return null;

        var card = bag.closest(SEL.card);
        if (!card || !this.contains(card)) return null;

        var plan = this.planShop(card);
        if (!plan) return null;

        plan.bag = bag;
        plan.card = card;
        return plan;
      }

      /* Everything the modal needs, or null for "let the link win".
         null happens for three legitimate reasons, and the store behaves
         identically in all three: the modal asset never loaded, the card has no
         payload, or the payload has nothing to sell. */
      planShop(card) {
        var api = window.SohoShopVideo;
        if (!api || typeof api.open !== 'function' || typeof api.readPayload !== 'function') return null;

        var payload;
        try {
          payload = api.readPayload(card);
        } catch (e) {
          return null;
        }
        if (!sellable(payload)) return null;

        /* The modal's prev/next arrows walk THIS row. Only siblings that can
           actually be shopped belong in the list, otherwise "next" lands on a
           video with an empty tray. The clicked card is in it — the modal needs
           to find itself in the group to know where the arrows point. Cheap
           presence test first, so cards without a payload are never parsed. */
        var scope = this.shopRoot || this.track || this;
        var siblings = Array.prototype.slice.call(scope.querySelectorAll(SEL.card));
        var group = [];
        siblings.forEach(function (other) {
          if (other === card) {
            group.push(other);
            return;
          }
          if (!other.querySelector(SEL.shopData)) return;
          var sibling;
          try {
            sibling = api.readPayload(other);
          } catch (e) {
            sibling = null;
          }
          if (sellable(sibling)) group.push(other);
        });

        var media = this.mediaFor(card);
        return { payload: payload, group: group, video: media.video, poster: media.poster };
      }

      /* The card's own video URL, so the modal plays the same film at full
         size instead of a black stage. Mounted cards have a real <video>;
         un-mounted ones still have their <template>, which is where the whole
         rail keeps its sources until hydration (a card below the fold has
         never had a <video> in the document). `.content` is guaranteed here:
         a browser without <template> cannot run customElements either, and
         soho-video-config.js already refuses to build cards without it. */
      mediaFor(card) {
        var out = { video: '', poster: '' };
        var source = card.querySelector('video');

        if (!source) {
          var tpl = card.querySelector(SEL.template);
          var frag = tpl && tpl.content;
          source = frag ? frag.querySelector('video') : null;
        }

        if (source) {
          /* The <source> list is read BEFORE currentSrc on purpose. A mounted
             <video> has already run resource selection, so currentSrc is
             whichever rendition the browser picked first — on a Shopify asset
             that can be the HLS manifest, which the modal's plain <video src>
             cannot play outside Safari. pickSource applies the same rule to a
             mounted card and to a card still folded up in its <template>, so
             the modal gets the same file either way. */
          out.video =
            pickSource(source.querySelectorAll('source')) ||
            source.getAttribute('src') ||
            source.currentSrc ||
            '';
          out.poster = source.getAttribute('poster') || '';
        }

        if (!out.poster) {
          /* Stills-only cards have no <video> at all, and the cover is the
             only frame there is. currentSrc first: the theme's image snippet
             ships a srcset, so `src` is the smallest rung, not the painted one. */
          var img =
            card.querySelector('.soho-reel__poster img') ||
            card.querySelector('[data-soho-reel-media] img');
          if (img) out.poster = img.currentSrc || img.getAttribute('src') || '';
        }

        return out;
      }

      /* The click has already been swallowed by the time we get here, so this
         function is not allowed to end with nothing happening. If open() says
         no — or throws, which a half-finished modal on a parallel branch can
         still do — we walk the shopper to the PDP by hand: the same
         destination the anchor had, just later. */
      launchShop(plan) {
        var opened = false;
        try {
          /* `!== false` on purpose. The contract has open() return true/false,
             and we have already established there IS something to sell; if a
             build of the modal forgets the return, treating undefined as
             "opened" beats navigating away over an open dialog. */
          opened =
            window.SohoShopVideo.open({
              payload: plan.payload,
              card: plan.card,
              group: plan.group,
              video: plan.video,
              poster: plan.poster,
              /* Belt and braces next to SHOP_CLOSE_EVENT: whichever of the two
                 the modal implements, this row hears the close exactly once —
                 onShopClose is idempotent. */
              onClose: this.onShopClose
            }) !== false;
        } catch (e) {
          opened = false;
        }

        if (!opened) {
          this.navigateTo(plan.bag);
          return;
        }

        this.shopTrigger = plan.bag;
        this.suspendPlayback();
        this.watchShopClose();
      }

      navigateTo(bag) {
        var href = bag ? bag.href || bag.getAttribute('href') : '';
        if (href) window.location.href = href;
      }

      /* Park every video THIS element is playing and remember which ones, so
         closing the modal restores the row exactly as the shopper left it —
         including the manual play a reduced-motion visitor started by tapping,
         which applyPolicy() would never bring back on its own.
         Queried live rather than off this.cards: the dashboard can have swapped
         the row's cards since connectedCallback ran. Still rooted at `this`,
         which is the file's hard rule — the nine set pages and the home hero
         own videos this element must never touch. */
      suspendPlayback() {
        if (this.suspended) return;

        var parked = [];
        var cards = Array.prototype.slice.call(this.querySelectorAll(SEL.card));
        cards.forEach(function (card) {
          var video = card.querySelector('video');
          if (video && !video.paused) {
            parked.push(video);
            video.pause();
          }
        });

        this.suspended = parked;
        this.shopOpen = true;
      }

      onShopOpenEvent() {
        /* Fired by the modal itself. For the row that opened it this is a
           no-op (suspendPlayback already ran); for any other row on the page it
           is the only notice it gets. */
        this.suspendPlayback();
        this.watchShopClose();
      }

      /* Reached from three directions — the document event, the onClose
         callback, and the watchdog — so it has to survive being called two or
         three times in a row. The state is cleared first and read from locals,
         which is what makes the second call cost nothing. */
      onShopClose() {
        if (!this.shopOpen && !this.suspended) return;

        this.stopShopWatch();
        this.shopOpen = false;

        var parked = this.suspended;
        this.suspended = null;

        if (parked) {
          parked.forEach(function (video) {
            /* The card can have been rebuilt underneath us; a detached <video>
               is not worth resuming and would never be seen. */
            if (!document.contains(video)) return;
            /* userInitiated: a close IS a gesture, and it also clears the
               autoplay-blocked flag on a video the shopper chose to play. */
            this.safePlay(video, true);
          }, this);
        }

        this.restoreShopFocus();
      }

      /* Focus restoration is the modal's job — contract section 7 hands it the
         card for exactly that. This only catches the case where nobody took
         focus at all: landing on <body> after a dialog closes strands a
         keyboard shopper at the top of the document, several hundred tab stops
         from the row they were shopping. */
      restoreShopFocus() {
        var bag = this.shopTrigger;
        this.shopTrigger = null;
        if (!bag || !document.contains(bag)) return;

        var active = document.activeElement;
        if (active && active !== document.body && active !== document.documentElement) return;

        try {
          bag.focus();
        } catch (e) {
          /* noop */
        }
      }

      /* Last resort, and the reason applyPolicy() can afford to stand down
         while the modal is up. The modal is a separate asset built in parallel;
         if the build on the store implements neither the close event nor the
         onClose callback, this row would otherwise stay silent until a reload.
         So we watch for the state class the contract already requires it to
         write, and treat its absence as closed.
         The grace window is what keeps the watchdog honest: a modal that mounts
         its root first and adds `.is-open` on the next frame must not be
         declared closed a tick after it opened. */
      watchShopClose() {
        this.stopShopWatch();
        if (!window.setInterval || !document.querySelector) return;

        var self = this;
        var started = Date.now();
        this.shopSawOpen = !!document.querySelector(SHOP_OPEN_ROOT);

        this.shopWatch = window.setInterval(function () {
          if (document.querySelector(SHOP_OPEN_ROOT)) {
            self.shopSawOpen = true;
            return;
          }
          if (!self.shopSawOpen && Date.now() - started < SHOP_WATCH_GRACE) return;
          self.onShopClose();
        }, SHOP_WATCH_TICK);
      }

      stopShopWatch() {
        if (this.shopWatch) {
          clearInterval(this.shopWatch);
          this.shopWatch = 0;
        }
        this.shopSawOpen = false;
      }
    }
  );
})();
